import type { BoardItemListItemResponse } from "@/client";
import { createMemo, createSignal, onMount } from "solid-js";
import { mapBoardClipsToVideoEvidence } from "~/modules/askgvt/boards/mapBoardClipsToVideoEvidence";
import { askPageVars } from "./pageVars";

type OpenTarget = {
	boardId: string;
	clipId: string;
};

const PROFILE_CLIP_PARAMS = ["board_id", "clip_id"] as const;

function syncProfileClipUrl(target: OpenTarget | null) {
	const url = new URL(window.location.href);
	if (target) {
		url.searchParams.set("board_id", target.boardId);
		url.searchParams.set("clip_id", target.clipId);
	} else {
		for (const key of PROFILE_CLIP_PARAMS) {
			url.searchParams.delete(key);
		}
	}
	const qs = url.searchParams.toString();
	window.history.replaceState(
		null,
		"",
		qs ? `${url.pathname}?${qs}` : url.pathname,
	);
}

function preloadedBoardClips(
	boardId: string,
): BoardItemListItemResponse[] | undefined {
	const clips = askPageVars().boards.find((board) => board.id === boardId)
		?.playback_clips;
	return clips?.length ? clips : undefined;
}

export function useAskPageBoardClipPlayback() {
	const [boardClips, setBoardClips] = createSignal<
		BoardItemListItemResponse[] | undefined
	>(undefined);
	const [activeBoardId, setActiveBoardId] = createSignal<string | undefined>(
		undefined,
	);
	const [videoIndex, setVideoIndex] = createSignal<number | undefined>(
		undefined,
	);
	const [error, setError] = createSignal<string | null>(null);

	const isOpen = createMemo(
		() =>
			boardClips() !== undefined &&
			videoIndex() !== undefined &&
			activeBoardId() !== undefined,
	);

	const videoEvidence = createMemo(() =>
		mapBoardClipsToVideoEvidence(boardClips()),
	);

	const openClip = (boardId: string, clipId: string) => {
		setError(null);
		const clips = preloadedBoardClips(boardId);
		if (!clips) {
			setError("Could not load board clips.");
			setBoardClips(undefined);
			setVideoIndex(undefined);
			setActiveBoardId(undefined);
			return;
		}

		const index = clips.findIndex((clip) => clip.id === clipId);
		if (index < 0) {
			setError("That clip is no longer on this board.");
			setBoardClips(undefined);
			setVideoIndex(undefined);
			setActiveBoardId(undefined);
			return;
		}
		setBoardClips(clips);
		setActiveBoardId(boardId);
		setVideoIndex(index);
		syncProfileClipUrl({ boardId, clipId });
	};

	const close = () => {
		setBoardClips(undefined);
		setVideoIndex(undefined);
		setActiveBoardId(undefined);
		setError(null);
		syncProfileClipUrl(null);
	};

	const setActiveVideoIndex = (index: number) => {
		const clips = boardClips();
		if (!clips || index < 0 || index >= clips.length) {
			return;
		}
		setVideoIndex(index);
		const boardId = activeBoardId();
		const clipId = clips[index]?.id;
		if (boardId && clipId) {
			syncProfileClipUrl({ boardId, clipId });
		}
	};

	onMount(() => {
		const url = new URL(window.location.href);
		const boardId = url.searchParams.get("board_id");
		const clipId = url.searchParams.get("clip_id");
		if (boardId && clipId) {
			openClip(boardId, clipId);
		}
	});

	return {
		isOpen,
		error,
		activeBoardId,
		videoIndex,
		videoEvidence,
		openClip,
		close,
		setActiveVideoIndex,
	};
}

export type AskPageBoardClipPlayback = ReturnType<
	typeof useAskPageBoardClipPlayback
>;
